import { Input, Select } from '../design-system';
import {
  INDIA_STATES, SELECTABLE_STATES, normalizeStateCode,
  stateByCode, stateNameByCode, stateCodeByName, stateLabel,
} from '../lib/indiaStates';

// The one state picker for the whole site.
//
// State code and state name are ONE fact, so this owns both: pick a state and
// it hands back `{ stateCode, state }` together. They used to be two free-text
// inputs sitting side by side in four different forms, which meant a client
// could be saved as "Maharashtra / 06" — and the code is what decides
// CGST+SGST vs IGST on every invoice, so that mismatch reaches a statutory
// document without anything having looked wrong on screen.
//
// Props:
//   code      current state code ('27'). The value of record.
//   name      current state name, if the caller stores one separately.
//   onChange  ({ stateCode, state }) => void — always both, always consistent.
//   label / disabled / required as usual.
export default function StateSelect({
  code,
  name,
  onChange,
  label = 'State',
  disabled = false,
  required = false,
  helperText,
}) {
  // The code wins when both are present — it is the field with statutory
  // meaning. A record holding only a legacy name still resolves to its code, so
  // this doesn't blank out data written before there was a picker.
  const resolved = normalizeStateCode(code) || stateCodeByName(name);
  const known = stateByCode(resolved);

  // A retired code (25 Daman and Diu, 28 undivided AP) is not in the offered
  // list but IS still on plenty of real registrations, so it gets an option of
  // its own rather than silently reading as "— Select state —" and being lost
  // the next time somebody saves the form.
  const legacy = known?.retired ? known : null;

  const emit = (nextCode) => {
    const c = normalizeStateCode(nextCode);
    onChange({ stateCode: c, state: stateNameByCode(c) });
  };

  return (
    <Select
      label={label}
      value={resolved}
      onChange={(e) => emit(e.target.value)}
      disabled={disabled}
      required={required}
      helperText={
        helperText
        ?? (legacy
          ? `Code ${legacy.code} is no longer issued — new registrations in this territory use ${legacy.supersededBy}.`
          : undefined)
      }
    >
      <option value="">— Select state —</option>
      {legacy && <option value={legacy.code}>{stateLabel(legacy.code)} (legacy)</option>}
      {SELECTABLE_STATES.map((s) => (
        <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
      ))}
    </Select>
  );
}

// The read-only twin, for where the state is DERIVED and must not be typed —
// principally beside a GSTIN, whose first two digits already are the code.
// Rendered as a disabled input rather than as plain text so it still reads as a
// field of the form, and so its absence is visible rather than invisible.
export function DerivedStateField({ code, label = 'State (from GSTIN)', emptyHint }) {
  const known = stateByCode(code);
  return (
    <Input
      label={label}
      value={known ? stateLabel(known.code) : ''}
      placeholder={emptyHint || 'Set automatically'}
      readOnly
      disabled
      helperText={
        known?.retired
          ? `Code ${known.code} is no longer issued — this registration predates the change.`
          : !known && code
            ? `"${code}" is not a valid GST state code.`
            : undefined
      }
    />
  );
}

// Re-exported so a caller needing the raw list doesn't have to import from two
// places to build one field.
export { INDIA_STATES, SELECTABLE_STATES };
